import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { faEnvelope, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Checkbox,
  Group,
  NativeSelect,
  Paper,
  Space,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";

import { UserPhoto } from "@components";
import { useUser } from "@hooks";

import { putUserEmail } from "@pages/dashboard";
import { useUserEmailSettings } from "@pages/settings/hooks";

export const AccountSettingsPanel: React.FC = () => {
  const user = useUser();
  const history = useHistory();

  const [selectedEmail, setSelectedEmail] = useState<string | null>(
    user?.email ?? null
  );
  const [subscribeChecked, setSubscribeChecked] = useState<boolean>(false);
  const [experimentsChecked, setExperimentsChecked] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const { data: emailSettings, refetch: refreshEmailSettings } =
    useUserEmailSettings();

  useEffect(() => {
    setSubscribeChecked(emailSettings?.subscribed ?? false);
    setExperimentsChecked(emailSettings?.contact_about_experiments ?? false);
  }, [emailSettings]);

  if (!user) {
    history.push("/login");
  }

  return (
    <>
      <Group mt="md" align="stretch">
        <UserPhoto profile_photo={user?.profile_photo} large />
        <Stack justify="center" align="flex-start" spacing={0}>
          <Text size="xl" weight="bold">
            {user?.username}
          </Text>
          <Text size="md">Account Settings</Text>
        </Stack>
      </Group>
      <Paper shadow="xs" p="md" mt="lg">
        <Tabs orientation="vertical" variant="pills" defaultValue="email">
          <Tabs.List pr="lg">
            <Tabs.Tab
              value="email"
              icon={<FontAwesomeIcon icon={faEnvelope} />}
            >
              Email
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="email">
            Email Settings
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (selectedEmail) {
                  setSaving(true);
                  putUserEmail(
                    selectedEmail,
                    subscribeChecked,
                    experimentsChecked,
                    false
                  ).then(() => {
                    refreshEmailSettings().then(() => {
                      setSaving(false);
                    });
                  });
                }
              }}
            >
              <NativeSelect
                label="Primary Email"
                value={selectedEmail ?? undefined}
                data={
                  // sometimes, available emails don't load. in that case, just show the primary email
                  user!.available_emails?.map((email) => email.email) ??
                  (user!.email ? [user!.email!] : [])
                }
                onChange={(e) => setSelectedEmail(e.target.value)}
                mt="sm"
                pb="md"
                sx={{ maxWidth: "320px" }}
              />
              <Checkbox
                label="Send me monthly updates about new features and changes"
                checked={subscribeChecked}
                onChange={(e) => setSubscribeChecked(e.currentTarget.checked)}
                pb="xs"
              />
              <Checkbox
                label="Get early access to unreleased features and experiments"
                checked={experimentsChecked}
                onChange={(e) => setExperimentsChecked(e.currentTarget.checked)}
              />
              <Space h="md" />
              <Button
                type="submit"
                mt="sm"
                disabled={saving}
                leftIcon={
                  saving ? (
                    <FontAwesomeIcon icon={faSpinnerThird} spin />
                  ) : undefined
                }
              >
                Update Settings
              </Button>
            </form>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </>
  );
};
